import { X } from "lucide-react";

import { Button } from "@/common/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Separator } from "@/common/ui/separator";

import type { AuditEventEntry } from "./audit-log-types";

interface AuditLogDetailPanelProps {
  entry: AuditEventEntry;
  onClose: () => void;
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

export function AuditLogDetailPanel({
  entry,
  onClose,
}: AuditLogDetailPanelProps): React.ReactElement {
  return (
    <Card data-testid="audit-detail-panel" className="h-fit">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Audit Event Details</CardTitle>
        <Button
          data-testid="close-audit-detail-button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DetailField
            label="Event ID"
            value={<span className="font-mono text-xs">{entry.id}</span>}
          />
          <DetailField label="Timestamp" value={new Date(entry.created_at).toLocaleString()} />
          <DetailField label="Action" value={entry.action} />
          <DetailField
            label="Actor"
            value={<span className="font-mono text-xs">{entry.actor_user_id ?? "N/A"}</span>}
          />
          <DetailField label="Target Type" value={entry.target_type ?? "N/A"} />
          <DetailField
            label="Target ID"
            value={<span className="font-mono text-xs">{entry.target_id ?? "N/A"}</span>}
          />
          <DetailField
            label="Enterprise ID"
            value={<span className="font-mono text-xs">{entry.enterprise_id ?? "N/A"}</span>}
          />
          <DetailField
            label="Org Unit ID"
            value={<span className="font-mono text-xs">{entry.org_unit_id ?? "N/A"}</span>}
          />
        </div>

        <Separator />

        <div>
          <span className="text-xs font-medium text-muted-foreground">Metadata</span>
          <pre
            data-testid="audit-metadata-json"
            className="mt-1 max-h-64 overflow-auto rounded border bg-muted/50 p-3 text-xs"
          >
            {JSON.stringify(entry.metadata, null, 2)}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
}
